import { HARDCODED_DATA_KEY } from "src/stores/models/data-key";
import { OnUpload } from "@types";
import Papa from "papaparse";

export const dataKeyToOnUpload: Record<string, OnUpload> = {
  [HARDCODED_DATA_KEY.PUBMED_SEARCH_STRING]: {
    onUpload: (file: File) => {
      return new Promise((resolve, reject) => {
        function mapToMarkdownTable(input: string) {
          // Split the input into records, each record separated by a double newline
          const records = input
            .split(/\r?\n\r?\n/)
            .filter(
              (record) =>
                record.includes("PMID-") &&
                record.includes("DP  -") &&
                record.includes("TI  -") &&
                record.includes("AB  -") &&
                record.includes("FAU -") &&
                record.includes("SO  -")
            );
          // Initialize the Markdown table with headers
          let markdownTable = `| ID | Publication Date | Title | Abstract | First Author | Citation |\n| ------ | ------ | ------ | ------ | ------ | ------ |\n`;

          records.forEach((record) => {
            const pmidMatch = record.match(/PMID- (\d+)/);
            const publicationDateMatch = record.match(/DP  - (.+)/);
            const titleMatch = record
              .match(/TI\s+-\s+([\s\S]+?)(?=\n[A-Z]{2,}\s+-)/)?.[1]
              .replace(/\r\n?\s*/g, "");
            const abstractMatch = record
              .match(/AB\s+-\s+([\s\S]+?)(?=\n[A-Z]{2,}\s+-)/)?.[1]
              .replace(/\r\n?\s*/g, "");
            const firstAuthorMatch = record.match(/FAU - (.+)/);
            const citationMatch = record
              .match(/SO\s+-\s+([\s\S]+)/)?.[1]
              .replace(/\r?\n\s*/g, " ")
              .trim();

            const pmid = pmidMatch ? pmidMatch[1].trim() : "";
            const publicationDate = publicationDateMatch
              ? publicationDateMatch[1].trim()
              : "";
            const title = titleMatch ? titleMatch.trim() : "";
            const abstract = abstractMatch ? abstractMatch.trim() : "";
            const firstAuthor = firstAuthorMatch
              ? firstAuthorMatch[1].trim()
              : "";
            const citation = citationMatch || "";

            markdownTable += `| ${pmid} | ${publicationDate} | ${title} | ${abstract} | ${firstAuthor} | ${citation} |\n`;
          });

          return markdownTable;
        }

        // retrieve the file text
        const reader = new FileReader();
        reader.onload = async (e) => {
          const text = e.target?.result as string;

          const markdown = mapToMarkdownTable(text);
          resolve({ type: "table", result: markdown });
        };

        reader.onerror = () => {
          // If there's an error reading the file, reject the promise
          reject(new Error("Failed to read file"));
        };

        reader.readAsText(file);
      });
    },
  },
  [HARDCODED_DATA_KEY.COCHRANE_SEARCH_STRING]: {
    onUpload: (file: File) => {
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          complete: (results) => {
            if (results.errors.length > 0) {
              console.error("Error parsing CSV", results.errors);
              reject(new Error("Error parsing CSV"));
            } else {
              const entries = results.data as string[][];

              let markdownTable = `| ID | Publication Date | Title | Abstract | First Author | Citation |\n| ------ | ------ | ------ | ------ | ------ | ------ |\n`;

              // Transform array to markdown table first item is an array of headers and the rest are rows
              markdownTable += entries
                .slice(1)
                .map((row) => {
                  // Authors in cochrane are exported like this: Beishon, LC; Batterham, AP; Quinn, TJ; Nelson, CP; Panerai, RB; Robinson, T; Haunton, VJ
                  // We only want the fist one

                  const authors = row[1]?.split(";");

                  // For some reason the last row sometimes is empty so we need to make sure we are not accessing out of bounds
                  const firstAuthor = authors
                    ? `${authors[0]} ${authors.length > 1 ? "et al." : ""}`
                    : "";

                  return `| ${row[0]} | ${row[4]} | ${row[2]} | ${
                    row[5]
                  } | ${firstAuthor} | ${row.at(-3)} |`;
                })
                .join("\n");

              resolve({ type: "table", result: markdownTable });
            }
          },
        });
      });
    },
  },
  [HARDCODED_DATA_KEY.CLINICAL_TRIAL_SEARCH_TABLE_RAW_CSV]: {
    onUpload: (
      file: File
    ): Promise<{ type: "table" | "json"; result: string }> => {
      return new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              const text = e.target?.result as string;

              const entries: Record<string, any>[] = JSON.parse(text);

              const mappedEntries = entries.map((e) => ({
                id: e.protocolSection.identificationModule.nctId,
                title: e.protocolSection.identificationModule.officialTitle,
                collaborators: e.protocolSection.sponsorCollaboratorsModule,
                description: e.protocolSection.descriptionModule,
                conditions: e.protocolSection.conditionsModule,
                armsInterventions: e.protocolSection.armsInterventionsModule,
                outcomes: e.protocolSection.outcomesModule,
                eligibility: e.protocolSection.eligibilityModule,
                locations: e.protocolSection.contactsLocationsModule.locations,
                results: e.resultsSection,
                hasResults: e.hasResults,
              }));

              resolve({
                type: "json",
                result: JSON.stringify(mappedEntries),
              });
            } catch (e) {
              reject(new Error("Error parsing JSON"));
            }
          };

          reader.onerror = () => {
            // If there's an error reading the file, reject the promise
            reject(new Error("Failed to read file"));
          };

          reader.readAsText(file);
        } catch (e) {
          reject(e);
        }
      });
    },
  },
};
